/**
 * CustomTable
 * 
 * columns: [
 *   { 
 *     key: String, 字段名称
 *     label: String, // header 标题
 *     width: Number, // 列宽度
 *     align: String, // 水平对齐方式
 *     headBackgroundColor: String, // header 背景颜色
 *     sortable: Boolean, // 排序
 *     render: Function // 内容渲染函数，需要返回 node
 *   },
 *   ...
 * ]
 * 
 * @Author: Focci
 * @Date: 2023-08-31 12:20:42
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-31 12:20:42
 */

'use client'

// import PropTypes from 'prop-types'
import { useState, useMemo } from 'react'
import { isFunction } from 'lodash-es'
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel
} from '@comp/material'
import NoData from '@comp/NoData'

function descendingComparator(a, b, orderBy) {
  return b[orderBy] < a[orderBy] 
    ? -1
    : b[orderBy] > a[orderBy] 
      ? 1
      : 0
}

function stableSort(array, order, orderBy) {
  const comparator = order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
  
  return array.map((el, index) => [el, index])
    .sort((a, b) => {
      const ord = comparator(a[0], b[0])
      if (ord !== 0) {
        return ord
      }
      return a[1] - b[1]
    })
    .map((el) => el[0])
}

function headCell({ col, headRender, padding }) {
  return (
    <div 
      style={{ 
        padding, 
        whiteSpace: 'nowrap',
        minWidth: col.width ? `${col.width}px` : 'auto',
        textAlign: col.align || 'left',
        fontSize: '.7rem'
      }}
    >
      {headRender(col)}
    </div>
  )
}

function SortableTableHead(props) {
  const { 
    order, orderBy, onRequestSort, columns, headRender, padding,
  } = props
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow>
        {columns.map((col) => (
          <TableCell
            key={col.key}
            id={col.key}
            padding="none"
            sortDirection={orderBy === col.key ? order : false}
            sx={{
              '&.MuiTableCell-root': {
                position: 'sticky',
                top: 0,
                background: col.headBackgroundColor || '#fff',
                zIndex: 1
              }
            }}
          >
            {col.sortable && (
              <TableSortLabel
                active={orderBy === col.key}
                direction={orderBy === col.key ? order : 'asc'}
                onClick={createSortHandler(col.key)}
                sx={{
                  '&.MuiButtonBase-root .MuiSvgIcon-root': {
                    opacity: 0.5
                  },
                  '&.MuiButtonBase-root.Mui-active': (theme) => ({
                    color: theme.palette.primary.main
                  }),
                  '&.MuiButtonBase-root.Mui-active .MuiSvgIcon-root': (theme) => ({
                    opacity: 1,
                    color: theme.palette.primary.main
                  })
                }}
              >
                {headCell({ headRender, col, padding })}
              </TableSortLabel>
            )}
            {!col.sortable && (
              headCell({ headRender, col, padding })
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

function CustomTableBody({
  data = [],
  columns = [],
  keyProp = '',
  padding = '',
  zebra = false,
  bordered = false,
}) {
  return (
    <TableBody>
      {data.map((row, index) => (
        <TableRow
          hover
          onClick={() => {}}
          key={row[keyProp] || index}
        >
          {columns.map((col, i) => {
            const udx = `xx${index}`
            return (
              <TableCell 
                padding="none" 
                key={col.key}
                {...(i === 0 ? { id: udx, component: 'th' } : { headers: `${col.key} ${udx}` })}
                sx={{
                  backgroundColor: zebra && index % 2 === 0 ? '#f8f8f8' : 'white',
                  borderBottom: bordered ? 'solid 1px #e6e6e6' : 'none',
                }}
              >
                <div 
                  style={{ 
                    padding, 
                    textAlign: col.align || 'left', 
                    fontSize: '.8rem' 
                  }}
                >
                  {
                    isFunction(col.render) 
                      ? col.render({ row, index, col })
                      : row[col.key]
                  }
                </div>
              </TableCell>
            )
          })}
        </TableRow>
      ))}
    </TableBody>
  )
}

export default function CustomTable({
  data = [],
  columns = [],
  size = 'small',
  sorter = stableSort, // 排序函数
  keyProp = 'id', // row key
  sortOrder = 'asc', // asc, desc
  defaultOrderProp = '', // 默认排序字段
  headRender = (col) => col.label, // 渲染标题
  padding = '0.5rem',
  zebra = false, // 是否有斑马线
  bordered = false, // 是否有边框
  height = 'auto', // 高度
}) {
  const [order, setOrder] = useState(sortOrder)
  const [orderBy, setOrderBy] = useState(defaultOrderProp)

  const handleRequestSort = (_e, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const visibleRows = useMemo(
    () => sorter(data, order, orderBy), 
    [data, order, orderBy, sorter]
  )

  return (
    <>
      <TableContainer
        sx={{ '&.MuiTableContainer-root': { maxHeight: height } }}
        className="custom-scroll-bar"
      >
        <Table size={size}>
          <SortableTableHead
            order={order}
            orderBy={orderBy}
            columns={columns}
            padding={padding}
            onRequestSort={handleRequestSort}
            headRender={headRender}
          />
          <CustomTableBody 
            data={visibleRows}
            keyProp={keyProp}
            zebra={zebra}
            bordered={bordered}
            padding={padding}
            columns={columns}
          />
        </Table>
      </TableContainer>
      {data.length === 0 && (
        <div className="bg-cf0 py-6">
          <NoData />
        </div>
      )}
    </>
  )
}
